import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { useSiteContacts } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { SiteContact } from '@/lib/types';

/**
 * 현장 연락처.
 *
 * <p><b>화면에서 거르지 않는다.</b> 타 협력사를 빼는 일은 서버가 SQL 로 끝냈다
 * (`ExternalSiteInfoMapper.findContacts`) — 두 곳이 거르면 어느 쪽이 진짜 경계인지 흐려지고,
 * 언젠가 화면 쪽 조건만 느슨해진 채 남는다. 여기 온 줄은 볼 수 있는 줄이다.
 *
 * <p>전화번호는 <b>`tel:` 링크</b>다. 이 화면을 보는 사람은 대개 현장에서 손에 들고 있고,
 * 번호를 눈으로 옮겨 적는 동안 한 자리씩 틀린다.
 */
export function ContactsPage() {
  const { siteId, siteName } = useSite();
  const contacts = useSiteContacts(siteId);
  const rows = contacts.data ?? [];
  const groups = groupByContactGroup(rows);

  return (
    <>
      <PageHeader title="연락처" subtitle={siteName} />
      <QueryState
        pending={contacts.isPending}
        error={contacts.isError}
        empty={rows.length === 0}
        emptyText="등록된 연락처가 없습니다."
      >
        <div className="flex flex-col gap-4">
          {groups.map(([group, members]) => (
            <section key={group}>
              <h2 className="mb-1.5 px-1 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                {group} · {members.length}
              </h2>
              <ul className="flex flex-col gap-2">
                {members.map((row) => (
                  <ContactCard key={row.id} row={row} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </QueryState>
    </>
  );
}

/**
 * 연락처 한 장.
 *
 * <p>제목은 담당자 이름이지만 <b>이름 없는 줄</b>이 실제로 있다 — 회사 대표번호만 적은 줄이다
 * (V98:38). 그때는 회사 이름이 제목이 되고, 아래 회사 줄은 같은 말을 두 번 하지 않는다.
 */
function ContactCard({ row }: { row: SiteContact }) {
  const meta = [row.contactName === null ? null : row.company, row.duty].filter((it) => it !== null);

  return (
    <li className="card">
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 truncate text-ui-md font-medium">
          {row.contactName ?? row.company ?? '담당자 미지정'}
        </span>
        {row.contactRank !== null && (
          <span className="shrink-0 text-ui-2xs" style={{ color: 'var(--text-secondary)' }}>
            {row.contactRank}
          </span>
        )}
      </div>
      {meta.length > 0 && (
        <div className="mt-0.5 truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
          {meta.join(' · ')}
        </div>
      )}

      <div className="mt-2 flex flex-col gap-1.5">
        {row.phone !== null && (
          <CallLink
            number={row.phone}
            label={row.extension === null ? row.phone : `${row.phone} (내선 ${row.extension})`}
          />
        )}
        {row.email !== null && (
          <a href={`mailto:${row.email}`} className="truncate text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
            {row.email}
          </a>
        )}
      </div>
    </li>
  );
}

/**
 * 거는 버튼.
 *
 * <p>44px 아래로 내리지 않는다(`index.css`) — 장갑 낀 손으로 누르는 자리라 그보다 작으면
 * 옆 줄이 같이 눌린다.
 *
 * <p>`tel:` 에 넣는 번호에서는 공백·괄호를 뗀다. 표시는 사람이 읽는 모양 그대로 두고 거는 값만
 * 다듬는다 — 기기에 따라 구분자를 번호의 일부로 읽어 연결이 안 되는 경우가 있다.
 */
function CallLink({ number, label }: { number: string; label: string }) {
  return (
    <a
      href={`tel:${number.replace(/[^\d+*#]/g, '')}`}
      className="btn-secondary justify-start gap-2 text-ui-md"
      style={{ minHeight: 44 }}
    >
      <span style={{ color: 'var(--accent)' }}>전화</span>
      <span className="min-w-0 truncate">{label}</span>
    </a>
  );
}

/**
 * 구분으로 묶는다. <b>서버가 준 순서를 그대로 쓴다</b> — SQL 이 발주처·감리·설계사·인허가·
 * 협력업체 순으로 세워 보내므로, 여기서 다시 정렬하면 두 곳이 순서를 다투게 된다.
 */
function groupByContactGroup(rows: SiteContact[]): Array<[string, SiteContact[]]> {
  const groups = new Map<string, SiteContact[]>();
  for (const row of rows) {
    const bucket = groups.get(row.contactGroup);
    if (bucket === undefined) groups.set(row.contactGroup, [row]);
    else bucket.push(row);
  }
  return [...groups];
}
